/** Minimal i18n `t` shape used by this helper (avoids coupling tests to TFunction). */
export type BookingSlotTranslateFn = (key: string, defaultValue: string) => string;

type AxiosLikeError = {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
};

const SLOT_MISMATCH_MESSAGE_FRAGMENTS = [
  'bookingSlotsConfig',
  'startTime must align',
  'durationInMinutes must',
] as const;

const STALE_LESSON_OPTION_MESSAGE_FRAGMENTS = [
  'does not belong to tutor',
  'not active or not assigned',
] as const;

export function isBookingSlotMismatchError(error: unknown): boolean {
  const status = (error as AxiosLikeError)?.response?.status;
  if (status !== 400) return false;
  const message = (error as AxiosLikeError)?.response?.data?.message ?? '';
  return SLOT_MISMATCH_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment));
}

export function isStaleLessonOptionError(error: unknown): boolean {
  const status = (error as AxiosLikeError)?.response?.status;
  if (status !== 400) return false;
  const message = (error as AxiosLikeError)?.response?.data?.message ?? '';
  return STALE_LESSON_OPTION_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment));
}

export function getBookingSlotMismatchErrorMessage(
  error: unknown,
  t: BookingSlotTranslateFn,
  fallbackKey: string,
  fallbackDefault: string,
): string {
  if (isBookingSlotMismatchError(error)) {
    return t(
      'bookingSteps.slotMismatch',
      'This start time or duration no longer matches the listing configuration. Pick another slot and try again.',
    );
  }
  if (isStaleLessonOptionError(error)) {
    return t(
      'tutorBooking.staleLessonOption',
      'This lesson option is no longer available for this tutor. Choose another option and try again.',
    );
  }
  const serverMessage = (error as AxiosLikeError)?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim()) {
    return serverMessage.trim();
  }
  return t(fallbackKey, fallbackDefault);
}
